import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { getOrganizationHeaders } from '../orgContext';

interface AssignmentSummary {
  id: number;
  title: string;
  type?: string | null;
  courseId?: number | null;
  courseName?: string | null;
  status?: string | null;
}

interface SubmissionRow {
  id: number;
  assignmentId: number;
  assignmentTitle: string;
  assignmentType?: string | null;
  courseId?: number | null;
  courseName?: string;
  studentName: string;
  studentEmail?: string | null;
  status: string;
  startedAt?: string | null;
  submittedAt?: string | null;
  updatedAt?: string | null;
  attemptNumber?: number | null;
}

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const normalizedStatus = (status?: string | null) => (status || '').trim().toLowerCase();

const toDisplayStatus = (status?: string | null) => {
  if (!status) {
    return 'Unknown';
  }

  return status
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const ManageSubmissions: React.FC = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<AssignmentSummary[]>([]);
  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [studentFilter, setStudentFilter] = useState('');

  const getToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const buildAuthHeaders = useCallback((token: string) => ({
    Authorization: `Bearer ${token}`,
    ...getOrganizationHeaders(),
  }), []);

  const loadSubmissions = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await getToken();
      const headers = buildAuthHeaders(token);
      const response = await axios.get(`${API_BASE_URL}/assignments/admin/submissions`, {
        headers,
      });

      const attempts = Array.isArray(response.data) ? response.data : [];
      const nextAssignments = new Map<number, AssignmentSummary>();

      attempts.forEach((attempt: any) => {
        const assignmentId = Number(attempt.assignmentId ?? attempt.assignment?.id ?? 0);
        if (!assignmentId) {
          return;
        }

        if (!nextAssignments.has(assignmentId)) {
          nextAssignments.set(assignmentId, {
            id: assignmentId,
            title: attempt.assignmentTitle || attempt.assignment?.title || 'Untitled assignment',
            type: attempt.assignmentType || attempt.assignment?.type || 'Assignment',
            courseId: attempt.courseId ?? attempt.assignment?.courseId ?? null,
            courseName: attempt.courseName || attempt.assignment?.courseName || 'General',
          });
        }
      });

      setAssignments(Array.from(nextAssignments.values()));

      const mappedRows = attempts.map((attempt: any) => ({
        id: Number(attempt.id),
        assignmentId: Number(attempt.assignmentId ?? attempt.assignment?.id ?? 0),
        assignmentTitle: attempt.assignmentTitle || attempt.assignment?.title || 'Untitled assignment',
        assignmentType: attempt.assignmentType || attempt.assignment?.type || 'Assignment',
        courseId: attempt.courseId ?? attempt.assignment?.courseId ?? null,
        courseName: attempt.courseName || attempt.assignment?.courseName || 'General',
        studentName: attempt.studentName || attempt.studentEmail || 'Unknown student',
        studentEmail: attempt.studentEmail || attempt.studentName || null,
        status: attempt.status || 'Pending',
        startedAt: attempt.startedAt || null,
        submittedAt: attempt.submittedAt || null,
        updatedAt: attempt.updatedAt || null,
        attemptNumber: attempt.attemptNumber ?? null,
      } as SubmissionRow));

      setRows(mappedRows.sort((left, right) => {
        const leftValue = left.submittedAt || left.updatedAt || left.startedAt || '';
        const rightValue = right.submittedAt || right.updatedAt || right.startedAt || '';
        return new Date(rightValue).getTime() - new Date(leftValue).getTime();
      }));
    } catch (loadError: any) {
      setError(loadError?.response?.data?.message || loadError?.message || 'Failed to load submissions.');
      setRows([]);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [buildAuthHeaders, getToken, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    void loadSubmissions();
  }, [isAuthenticated, loadSubmissions]);

  const courseOptions = useMemo(() => {
    const names = new Map<number, string>();

    assignments.forEach((assignment) => {
      if (assignment.courseId && !names.has(assignment.courseId)) {
        names.set(assignment.courseId, assignment.courseName || `Course ${assignment.courseId}`);
      }
    });

    return Array.from(names.entries()).map(([id, name]) => ({ id, name }));
  }, [assignments]);

  const filteredRows = useMemo(() => {
    const normalizedStudent = studentFilter.trim().toLowerCase();
    const selectedStatus = normalizedStatus(statusFilter);

    return rows.filter((row) => {
      if (selectedStatus !== 'all' && normalizedStatus(row.status) !== selectedStatus) {
        return false;
      }

      if (assignmentFilter !== 'all' && row.assignmentId !== Number(assignmentFilter)) {
        return false;
      }

      if (courseFilter !== 'all' && row.courseId !== Number(courseFilter)) {
        return false;
      }

      if (normalizedStudent && !row.studentName.toLowerCase().includes(normalizedStudent)) {
        return false;
      }

      return true;
    });
  }, [assignmentFilter, courseFilter, rows, statusFilter, studentFilter]);

  const stats = useMemo(() => {
    const totalAttempts = rows.length;
    const submitted = rows.filter((row) => normalizedStatus(row.status) === 'submitted').length;
    const graded = rows.filter((row) => normalizedStatus(row.status) === 'graded').length;
    const pending = totalAttempts - submitted - graded;

    return { totalAttempts, submitted, graded, pending };
  }, [rows]);

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: 720, margin: '32px auto', padding: 16 }}>
        <h2 style={{ marginBottom: 12 }}>Submissions</h2>
        <p style={{ marginBottom: 16 }}>Please sign in to view submissions.</p>
        <button
          onClick={() => navigate('/')} 
          style={{
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '10px 16px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Go home
        </button>
      </div>
    );
  }

  const actions = [
    {
      key: 'back-to-assignments',
      label: 'Back to assignments',
      variant: 'secondary' as const,
      onClick: () => navigate('/manage-assignments'),
    },
    {
      key: 'refresh',
      label: 'Refresh',
      variant: 'secondary' as const,
      onClick: () => void loadSubmissions(),
      disabled: loading,
    },
  ];

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 0, alignItems: 'start' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 15,
          background: 'rgba(248, 250, 252, 0.96)',
          backdropFilter: 'blur(6px)',
          paddingTop: 0,
          paddingBottom: 0,
          display: 'grid',
          gap: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            padding: 0,
          }}
        >
          <div>
            <div style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: '#62769a', fontWeight: 700 }}>
              Submissions
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: '#5b6d89' }}>
              {`${stats.totalAttempts} attempts • ${stats.submitted} submitted • ${stats.graded} graded`}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {actions.map((action) => (
              <button
                key={action.key}
                type="button"
                onClick={action.onClick}
                disabled={action.disabled}
                style={{
                  border: '1px solid #cfe0ff',
                  background: action.variant === 'secondary' ? '#fff' : '#1f5aa6',
                  color: action.variant === 'secondary' ? '#1d3967' : '#fff',
                  borderRadius: 8,
                  padding: '8px 12px',
                  cursor: action.disabled ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  opacity: action.disabled ? 0.7 : 1,
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>

        {(error || loading) && (
          <div
            style={{
              borderRadius: 10,
              border: `1px solid ${error ? '#fecaca' : '#dfe8f8'}`,
              background: error ? '#fff1f2' : '#f8fbff',
              color: error ? '#b91c1c' : '#35568a',
              padding: '8px 10px',
              fontSize: '0.82rem',
              marginTop: 0,
            }}
          >
            {error || (loading ? 'Loading submissions...' : '')}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8, marginTop: 0 }}>
          <StatCard label="Total attempts" value={String(stats.totalAttempts)} color="#1f5aa6" />
          <StatCard label="Submitted" value={String(stats.submitted)} color="#2b8a3e" />
          <StatCard label="Graded" value={String(stats.graded)} color="#8f5d0e" />
          <StatCard label="Pending" value={String(stats.pending)} color="#a63e3e" />
        </div>
      </div>

      <div
        style={{
          background: '#fff',
          border: '1px solid #e3eaf5',
          borderRadius: 12,
          padding: 16,
          boxShadow: '0 4px 18px rgba(14, 35, 62, 0.04)',
          marginTop: 0,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start', justifyContent: 'flex-start', marginBottom: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180, fontSize: 12, fontWeight: 600, color: '#56657d' }}>
            Course
            <select value={courseFilter} onChange={(event) => setCourseFilter(event.target.value)} style={fieldStyle}>
              <option value="all">All courses</option>
              {courseOptions.map((course) => (
                <option key={course.id} value={String(course.id)}>
                  {course.name}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160, fontSize: 12, fontWeight: 600, color: '#56657d' }}>
            Status
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} style={fieldStyle}>
              <option value="all">All statuses</option>
              <option value="submitted">Submitted</option>
              <option value="graded">Graded</option>
              <option value="pending">Pending</option>
              <option value="in progress">In Progress</option>
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180, fontSize: 12, fontWeight: 600, color: '#56657d' }}>
            Assignment
            <select value={assignmentFilter} onChange={(event) => setAssignmentFilter(event.target.value)} style={fieldStyle}>
              <option value="all">All assignments</option>
              {assignments.map((assignment) => (
                <option key={assignment.id} value={String(assignment.id)}>
                  {assignment.title}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220, fontSize: 12, fontWeight: 600, color: '#56657d' }}>
            Student
            <input
              value={studentFilter}
              onChange={(event) => setStudentFilter(event.target.value)}
              placeholder="Search student name"
              style={fieldStyle}
            />
          </label>
        </div>

        {!loading && !error && (
          <>
            {filteredRows.length === 0 ? (
              <div style={{ padding: '12px 0', color: '#5d6c82' }}>No submissions match the current filters.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: '#f5f8fd', color: '#18325d' }}>
                      <th style={thStyle}>Student</th>
                      <th style={thStyle}>Assignment</th>
                      <th style={thStyle}>Course</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Submitted</th>
                      <th style={thStyle}>Attempt</th>
                      <th style={thStyle}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={`${row.assignmentId}-${row.id}`} style={{ borderTop: '1px solid #edf1f8' }}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600 }}>{row.studentName}</div>
                          {row.studentEmail && <div style={{ color: '#6580a7', fontSize: 12 }}>{row.studentEmail}</div>}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600 }}>{row.assignmentTitle}</div>
                          {row.assignmentType && <div style={{ color: '#6580a7', fontSize: 12 }}>{row.assignmentType}</div>}
                        </td>
                        <td style={tdStyle}>{row.courseName || 'General'}</td>
                        <td style={tdStyle}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: 999,
                              background: getStatusTone(row.status),
                              color: '#123160',
                              fontWeight: 600,
                              fontSize: 12,
                            }}
                          >
                            {toDisplayStatus(row.status)}
                          </span>
                        </td>
                        <td style={tdStyle}>{formatDateTime(row.submittedAt || row.updatedAt || row.startedAt)}</td>
                        <td style={tdStyle}>{row.attemptNumber ?? '—'}</td>
                        <td style={tdStyle}>
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/submissions/${row.id}`)}
                            style={{
                              background: '#1f5aa6',
                              color: '#fff',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontWeight: 600,
                            }}
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: string;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color }) => (
  <div
    style={{
      background: '#fff',
      border: '1px solid #e3eaf5',
      borderRadius: 10,
      padding: '10px 12px',
      boxShadow: '0 3px 10px rgba(14, 35, 62, 0.03)',
      minHeight: 74,
    }}
  >
    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: '#5d6c82', lineHeight: 1.2 }}>{label}</div>
    <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, lineHeight: 1.2, color }}>{value}</div>
  </div>
);

const fieldStyle: React.CSSProperties = {
  border: '1px solid #dfe7f2',
  borderRadius: 8,
  padding: '9px 10px',
  minHeight: 40,
  fontSize: 14,
  color: '#18325d',
  background: '#fff',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 10px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
};

const tdStyle: React.CSSProperties = {
  padding: '12px 10px',
  verticalAlign: 'top',
  color: '#18325d',
};

const getStatusTone = (status?: string | null) => {
  const normalized = (status || '').toLowerCase();

  if (normalized === 'submitted') {
    return '#dfeff3';
  }

  if (normalized === 'graded') {
    return '#fdf1d8';
  }

  if (normalized === 'pending' || normalized === 'in progress') {
    return '#fdeaea';
  }

  return '#edf3ff';
};

export default ManageSubmissions;
